<template>
    <Modal
        ref="modal"
        title="Invoice Payments"
        size="is-4xlarge"
        :loading="isLoading"
        paddingless
        :close-enable="!isSupplierRaised"
        @closed="resetModal()"
    >
        <div class="tw-overflow-x-auto">
            <table class="payment-dates-table tw-w-full tw-min-w-[920px]">
                <colgroup>
                    <col v-if="hasExternalPayments" class="tw-w-16" />
                    <col class="tw-w-32" />
                    <col class="tw-w-[210px]" />
                    <col class="tw-w-[180px]" />
                    <col v-if="isConnectedToXero" class="tw-w-[280px]" />
                    <col
                        v-if="usesConversionRate && invoice?.target_currency !== invoice?.base_currency"
                        class="tw-w-[180px]"
                    />
                    <col class="tw-w-[110px]" />
                    <col class="tw-w-20" />
                </colgroup>
                <thead>
                    <tr>
                        <th v-if="hasExternalPayments" scope="col">
                            <span class="tw-sr-only">Integration</span>
                        </th>
                        <th scope="col">Sync status</th>
                        <th scope="col">Date</th>
                        <th scope="col">Amount</th>
                        <th v-if="isConnectedToXero" scope="col">Xero bank account</th>
                        <th
                            v-if="usesConversionRate && invoice?.target_currency !== invoice?.base_currency"
                            scope="col"
                        >
                            Conversion rate
                        </th>
                        <th scope="col">Paid</th>
                        <th scope="col" class="tw-text-right">
                            <span class="tw-sr-only">Actions</span>
                        </th>
                    </tr>
                </thead>
                <tbody v-if="paymentDates.length">
                    <tr v-for="(row, index) in paymentDates" :key="row.id ?? `payment-date-${index}`">
                        <td v-if="hasExternalPayments" class="tw-text-center">
                            <Tooltip v-if="paymentIntegration(row)" :label="paymentIntegration(row).tooltip">
                                <span class="tw-flex tw-justify-center">
                                    <img
                                        :src="paymentIntegration(row).logo"
                                        :alt="`${paymentIntegration(row).name} Logo`"
                                        class="tw-h-7 tw-w-7 tw-object-contain"
                                    />
                                </span>
                            </Tooltip>
                        </td>
                        <td>
                            <Tag :type="paymentSyncStatusType(row)" compact>
                                {{ paymentSyncStatusLabel(row) }}
                            </Tag>
                        </td>
                        <td>
                            <VueFlatPickr
                                v-model="row.date"
                                :placeholder="row.date"
                                :config="flatpickrConfig"
                                :disabled="isPaymentLocked(row)"
                                class="is-small tw-w-full"
                                required
                            />
                        </td>
                        <td>
                            <PercentageValue
                                v-if="usesConversionRate"
                                v-model="row.amount_target"
                                :base-value="invoice.total_value_target"
                                :disabled="isPaymentLocked(row)"
                                :step="0.0001"
                                size="is-small"
                                :currency-code="invoice.target_currency"
                            />
                            <PercentageValue
                                v-else
                                v-model="row.amount"
                                :base-value="invoice.total_value"
                                :disabled="isPaymentLocked(row)"
                                :currency-code="invoice.target_currency"
                                :step="0.0001"
                                size="is-small"
                            />
                        </td>
                        <td v-if="isConnectedToXero">
                            <Select
                                v-model="row.xero_account_id"
                                placeholder="Please select a bank account"
                                :disabled="isPaymentLocked(row)"
                                :loading="isLoadingXeroAccounts"
                                class="tw-w-full"
                            >
                                <option
                                    v-for="xeroAccount in filteredXeroAccounts"
                                    :key="xeroAccount.id"
                                    :value="xeroAccount.id"
                                >
                                    {{
                                        xeroAccount.code
                                            ? `${xeroAccount.code} - ${xeroAccount.name}`
                                            : xeroAccount.name
                                    }}
                                </option>
                            </Select>
                        </td>
                        <td v-if="usesConversionRate && invoice?.target_currency !== invoice?.base_currency">
                            <NumberInput
                                v-model="row.conversion_rate"
                                min="0"
                                class="tw-w-full"
                                :disabled="isPaymentLocked(row)"
                            />
                        </td>
                        <td>
                            <Checkbox v-model="row.is_paid" :disabled="isPaymentLocked(row)">Paid</Checkbox>
                        </td>
                        <td class="tw-text-right">
                            <div class="tw-flex tw-justify-end tw-gap-2">
                                <Tooltip v-if="canRetryPayment(row)" :label="retryPaymentLabel(row)" position="left">
                                    <Button
                                        type="is-info"
                                        compact
                                        :loading="retryingPaymentId === row.id"
                                        :disabled="retryingPaymentId !== null"
                                        :aria-label="retryPaymentButtonLabel(row)"
                                        @click="retryPaymentDate(row)"
                                    >
                                        <i class="mdi mdi-refresh"></i>
                                    </Button>
                                </Tooltip>
                                <Tooltip
                                    :disabled="!paymentLockReason(row)"
                                    :label="paymentLockReason(row)"
                                    position="left"
                                >
                                    <Button
                                        type="is-danger"
                                        compact
                                        :disabled="isPaymentLocked(row)"
                                        aria-label="Remove payment date"
                                        @click="removePaymentDate(index)"
                                    >
                                        <i class="mdi mdi-close"></i>
                                    </Button>
                                </Tooltip>
                            </div>
                        </td>
                    </tr>
                </tbody>
                <tbody v-else>
                    <tr>
                        <td :colspan="visibleColumnCount" class="!tw-p-0">
                            <p class="empty-label tw-m-0 tw-py-8">No payment dates have been created</p>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div class="tw-flex tw-flex-col tw-gap-3 tw-p-4">
            <Button v-if="!invoice || !invoice.is_pooled" type="is-link" class="tw-w-full" @click="addPaymentDate()">
                <i class="mdi mdi-plus tw-mr-1"></i>
                New payment date
            </Button>

            <Banner v-if="invoice && invoice.is_pooled" type="is-info">
                <div class="tw-flex tw-items-center tw-gap-4">
                    <i class="mdi mdi-information tw-text-3xl"></i>
                    <p class="tw-mb-0 tw-flex-1">
                        This invoice is synced from a pooled Xero account. Payment dates are managed automatically and
                        cannot be edited.
                    </p>
                </div>
            </Banner>

            <Banner v-else-if="isAcumaticaInvoice && isConnectedToAcumatica" type="is-info">
                <div class="tw-flex tw-items-center tw-gap-4">
                    <img src="@/images/brands/acumatica-icon.svg" class="tw-h-8 tw-w-8" alt="Acumatica Logo" />
                    <p class="tw-mb-0 tw-flex-1">
                        Payments for invoices imported from Acumatica are pulled in automatically. Changes made in
                        Acumatica will be reflected here when the next update is received.
                    </p>
                </div>
            </Banner>

            <Banner
                v-else-if="invoice && isConnectedToXero && invoice.invoice_guid && invoice.invoice_status === 'DRAFT'"
                type="is-warning"
            >
                <div class="tw-flex tw-items-center tw-gap-4">
                    <i class="mdi mdi-alert tw-text-3xl"></i>
                    <p class="tw-mb-0 tw-flex-1">
                        Your invoice is currently marked as draft in Xero.
                        <br />
                        When marking a payment as paid, your invoice will be authorised automatically.
                    </p>
                </div>
            </Banner>

            <Banner v-else-if="isConnectedToXero" type="is-info">
                <div class="tw-flex tw-items-center tw-gap-4">
                    <img src="@/images/brands/xero_blue.png" class="tw-h-8 tw-w-8" alt="Xero Logo" />
                    <p class="tw-mb-0 tw-flex-1">
                        <strong>You&apos;re connected to Xero!</strong>
                        <br />
                        We&apos;ll sync scheduled dates and the next planned payment details to this Xero invoice. When
                        a payment is marked as paid, we sync the actual payment to Xero and bring payments from Xero
                        back into EventWise.
                    </p>
                </div>
            </Banner>
        </div>

        <template #footer>
            <Button :disabled="isSupplierRaised" @click="closeModal()">Cancel</Button>
            <Button v-if="usesConversionRate" @click="openCurrencyConverter()">
                <i class="mdi mdi-calculator"></i>
            </Button>

            <FormField v-if="invoice" label="Invoice Value" class="tw-ml-2">
                {{ formatCurrency(invoiceAmount - totalCredits, 2, invoice.target_currency) }}
            </FormField>

            <FormField
                v-if="invoice"
                label="Total Payments"
                class="tw-ml-auto tw-mr-4 tw-text-right"
                :class="{ 'tw-text-red-400': paymentsExceedBalance }"
            >
                {{ formatCurrency(totalPayments, 2, invoice.target_currency) }}
            </FormField>

            <Button type="is-success" :disabled="saveDisabled" @click="upsertPaymentDates()">Save Changes</Button>
        </template>
    </Modal>
</template>

<script setup>
import { computed, ref, useTemplateRef } from 'vue';
import axios from 'axios';
import { route } from 'ziggy-js';
import VueFlatPickr from 'vue-flatpickr-component';
import { useDataStore } from '@/js/stores/DataStore.js';
import { flatpickrConfig, formatCurrency, toast } from '@/js/utils.js';
import { Eventbus } from '@/js/utils/eventbus.ts';
import { capturePosthogEvent } from '@/js/composables/usePosthog.js';
import Modal from '@/js/components/Modal.vue';
import Tooltip from '@/js/components/Tooltip.vue';
import Checkbox from '@/js/components/controls/Checkbox.vue';
import Select from '@/js/components/controls/Select.vue';
import PercentageValue from '@/js/components/widgets/PercentageValue.vue';
import Button from '@/js/components/controls/Button.vue';
import Banner from '@/js/components/Layout/Banner.vue';
import FormField from '@/js/components/widgets/FormField.vue';
import NumberInput from '@/js/components/controls/NumberInput.vue';
import Tag from '@/js/components/Tag.vue';
import xeroLogo from '@/images/brands/xero_blue.png';
import acumaticaLogo from '@/images/brands/acumatica-icon.svg';

const emit = defineEmits(['closed']);
const store = useDataStore();

const isLoading = ref(false);
const isLoadingXeroAccounts = ref(false);
const event = ref(null);
const purchaseOrder = ref(null);
const revenueEntry = ref(null);
const invoice = ref(null);
const paymentDates = ref([]);
const isSupplierRaised = ref(false);
const xeroAccounts = ref([]);
const retryingPaymentId = ref(null);
const modal = useTemplateRef('modal');

const isConnectedToXero = computed(() => Boolean(store.get('account.is_connected_to_xero')));
const isConnectedToAcumatica = computed(() =>
    Boolean(store.get('account.is_connected_to_acumatica') && store.get('account.integrations.acumatica'))
);
const isAcumaticaInvoice = computed(() => invoice.value?.integrator === 'acumatica');
const isXeroInvoice = computed(() => !purchaseOrder.value || invoice.value?.integrator === 'xero');
const usesConversionRate = computed(() => Boolean(invoice.value?.conversion_rate));

const invoiceAmount = computed(() => {
    if (!invoice.value) {
        return 0;
    }

    return parseFloat(usesConversionRate.value ? invoice.value.total_value_target : invoice.value.total_value) || 0;
});

const totalCredits = computed(() => {
    return (invoice.value?.credit_notes ?? []).reduce(
        (total, creditNote) => total + (parseFloat(creditNote.amount_target) || 0),
        0
    );
});

const totalPayments = computed(() => {
    return paymentDates.value.reduce((total, paymentDate) => {
        const amount = usesConversionRate.value ? paymentDate.amount_target : paymentDate.amount;

        return total + (parseFloat(amount) || 0);
    }, 0);
});

const payableTotal = computed(() => invoiceAmount.value - totalCredits.value);

const paymentsExceedBalance = computed(() => {
    return parseFloat(totalPayments.value.toFixed(2)) > parseFloat(payableTotal.value.toFixed(2));
});

const saveDisabled = computed(() => !invoice.value || paymentsExceedBalance.value);

const filteredXeroAccounts = computed(() => {
    if (!isConnectedToXero.value) {
        return [];
    }

    return xeroAccounts.value.filter((xeroAccount) => xeroAccount.type === 'BANK');
});

const hasExternalPayments = computed(() => {
    return paymentDates.value.some((paymentDate) => Boolean(paymentDate.external_id));
});

const visibleColumnCount = computed(() => {
    return (
        5 +
        Number(hasExternalPayments.value) +
        Number(isConnectedToXero.value) +
        Number(usesConversionRate.value && invoice.value?.target_currency !== invoice.value?.base_currency)
    );
});

const paymentSyncStatus = {
    un_pushed: {
        label: 'Not pushed',
        type: 'info',
    },
    pushing: {
        label: 'Pushing…',
        type: 'info',
    },
    pushed: {
        label: 'Pushed',
        type: 'success',
    },
    failed: {
        label: 'Failed',
        type: 'danger',
    },
};

const normalizedPaymentSyncStatus = (paymentDate) => {
    return paymentDate?.sync_status ?? 'un_pushed';
};

const paymentSyncStatusLabel = (paymentDate) => {
    const status = normalizedPaymentSyncStatus(paymentDate);
    if (isXeroInvoice.value && !paymentDate?.is_paid && !paymentDate?.external_id) {
        return (
            {
                un_pushed: 'Plan not synced',
                pushing: 'Syncing plan…',
                pushed: 'Plan synced',
                failed: 'Plan sync failed',
            }[status] ?? 'Plan not synced'
        );
    }

    return paymentSyncStatus[status]?.label ?? 'Not pushed';
};

const paymentSyncStatusType = (paymentDate) => {
    return paymentSyncStatus[normalizedPaymentSyncStatus(paymentDate)]?.type ?? 'info';
};

const paymentIntegration = (paymentDate) => {
    if (!paymentDate?.external_id) {
        return null;
    }

    const source = String(paymentDate.external_source ?? '').toLowerCase();
    if (source === 'acumatica') {
        return {
            name: 'Acumatica',
            logo: acumaticaLogo,
            tooltip: `Acumatica payment\nRemote ID: ${paymentDate.external_id}`,
        };
    }

    if (source === 'xero' || paymentDate.xero_account_id) {
        return {
            name: 'Xero',
            logo: xeroLogo,
            tooltip: `Xero payment\nRemote ID: ${paymentDate.external_id}`,
        };
    }

    return null;
};

const isPaymentLocked = (paymentDate) => Boolean(paymentDate?.is_pooled || paymentDate?.external_id);

const paymentLockReason = (paymentDate) => {
    if (paymentDate?.is_pooled) {
        return 'This payment date is synced from a pooled Xero account and cannot be edited.';
    }

    const integration = paymentIntegration(paymentDate);
    if (integration) {
        return `This payment date has been synced with ${integration.name} - please make amendments in ${integration.name} so they pull through to Eventwise.`;
    }

    return paymentDate?.external_id
        ? 'This payment date is synced with an external system and cannot be edited.'
        : null;
};

const paymentSyncIntegration = (paymentDate) => {
    const externalIntegration = paymentIntegration(paymentDate);
    if (externalIntegration) {
        return externalIntegration;
    }

    if (purchaseOrder.value && isAcumaticaInvoice.value) {
        return {
            name: 'Acumatica',
        };
    }

    return {
        name: 'Xero',
    };
};

const canRetryPayment = (paymentDate) => {
    if (
        !paymentDate?.id ||
        !['un_pushed', 'failed'].includes(normalizedPaymentSyncStatus(paymentDate)) ||
        paymentDate.is_pooled ||
        paymentDate.external_source ||
        paymentDate.external_id
    ) {
        return false;
    }

    if (purchaseOrder.value && !paymentDate.is_paid) {
        return false;
    }

    return isXeroInvoice.value && isConnectedToXero.value && Boolean(invoice.value?.invoice_guid);
};

const retryPaymentLabel = (paymentDate) => {
    return paymentDate?.is_paid
        ? 'Retry pushing payment to ' + paymentSyncIntegration(paymentDate).name
        : 'Retry syncing payment plan to ' + paymentSyncIntegration(paymentDate).name;
};

const retryPaymentButtonLabel = (paymentDate) => {
    return paymentDate?.is_paid ? 'Retry payment sync' : 'Retry payment plan sync';
};

const loadXeroAccounts = async () => {
    if (!isConnectedToXero.value) {
        xeroAccounts.value = [];
        return;
    }

    isLoadingXeroAccounts.value = true;
    try {
        const { data } = await axios.get(route('api.account.xero.accounts'));
        xeroAccounts.value = data;
    } catch (error) {
        const message =
            error?.response?.data?.message ??
            'We were unable to load Xero accounts, check your Xero connection and try again';
        toast('Error', message, 'error');
    } finally {
        isLoadingXeroAccounts.value = false;
    }
};

const invoiceRouteParams = () => {
    if (purchaseOrder.value) {
        return event.value
            ? {
                  event: event.value,
                  purchaseOrder: purchaseOrder.value,
                  supplierInvoice: invoice.value,
              }
            : {
                  purchaseOrder: purchaseOrder.value,
                  supplierInvoice: invoice.value,
              };
    }

    return event.value
        ? {
              event: event.value,
              revenueEntry: revenueEntry.value,
              customerInvoice: invoice.value,
          }
        : {
              revenueEntry: revenueEntry.value,
              customerInvoice: invoice.value,
          };
};

const invoiceUrl = () => {
    return purchaseOrder.value
        ? route('api.account.event.purchaseorders.invoices.get', invoiceRouteParams())
        : route('api.account.event.revenue.invoices.get', invoiceRouteParams());
};

const paymentUpdateUrl = () => {
    return purchaseOrder.value
        ? route('api.account.event.purchaseorders.invoices.payments.update', invoiceRouteParams())
        : route('api.account.event.revenue.invoices.payments.update', invoiceRouteParams());
};

const paymentRetryUrl = (paymentDate) => {
    return purchaseOrder.value
        ? route('api.account.event.purchaseorders.invoices.payments.retry', {
              ...invoiceRouteParams(),
              paymentDate,
          })
        : route('api.account.event.revenue.invoices.payments.retry', {
              ...invoiceRouteParams(),
              customerPaymentDate: paymentDate,
          });
};

const openModal = (payload) => {
    event.value = payload.event;
    purchaseOrder.value = payload.purchaseOrder;
    revenueEntry.value = payload.revenueEntry;
    invoice.value = payload.invoice;
    isSupplierRaised.value = Boolean(payload.isSupplierRaised);

    reloadData();
    loadXeroAccounts();
    modal.value?.open();
};

const closeModal = () => {
    resetModal();
    modal.value?.close();
};

const resetModal = () => {
    isLoading.value = false;
    purchaseOrder.value = null;
    revenueEntry.value = null;
    invoice.value = null;
    paymentDates.value = [];
    isSupplierRaised.value = false;
    xeroAccounts.value = [];
    retryingPaymentId.value = null;
    emit('closed');
};

const retryPaymentDate = async (paymentDate) => {
    if (!canRetryPayment(paymentDate)) {
        return;
    }

    retryingPaymentId.value = paymentDate.id;
    try {
        const response = await axios.post(paymentRetryUrl(paymentDate));
        const paymentDateIndex = paymentDates.value.findIndex(({ id }) => id === paymentDate.id);
        if (paymentDateIndex !== -1) {
            paymentDates.value[paymentDateIndex] = response.data;
        }
        toast(
            paymentDate.is_paid ? 'Payment sync started' : 'Payment plan sync started',
            paymentDate.is_paid
                ? "We're attempting to push this payment to " + paymentSyncIntegration(paymentDate).name
                : "We're attempting to sync this payment plan to " + paymentSyncIntegration(paymentDate).name,
            'success'
        );
    } catch (error) {
        const message = error?.response?.data?.message ?? 'We were unable to retry this payment sync';
        toast('Payment sync failed', message, 'error');
    } finally {
        retryingPaymentId.value = null;
    }
};

const reloadData = async () => {
    isLoading.value = true;

    try {
        const response = await axios.get(invoiceUrl());
        invoice.value = response.data;
        paymentDates.value = response.data.payment_dates ?? [];
    } catch {
        toast('Error', 'An unexpected error occurred whilst loading invoice data', 'error');
    } finally {
        isLoading.value = false;
    }
};

const upsertPaymentDates = async () => {
    isLoading.value = true;
    const paymentEventName = purchaseOrder.value
        ? 'supplier_invoice_payment_added'
        : revenueEntry.value
          ? 'customer_invoice_payment_added'
          : null;
    const newPaymentCount = paymentEventName ? paymentDates.value.filter((paymentDate) => !paymentDate.id).length : 0;

    try {
        const response = await axios.post(paymentUpdateUrl(), {
            payment_dates: paymentDates.value.map((paymentDate) => ({
                ...paymentDate,
                amount: usesConversionRate.value
                    ? paymentDate.amount_target / invoice.value.conversion_rate
                    : paymentDate.amount,
            })),
        });
        paymentDates.value = response.data;
        if (paymentEventName) {
            Array.from({ length: newPaymentCount }).forEach(() => {
                capturePosthogEvent(paymentEventName);
            });
        }
        closeModal();
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error updating payment dates:', error);
    } finally {
        isLoading.value = false;
    }
};

const currentDate = () => {
    const date = new Date();
    const pad = (value) => String(value).padStart(2, '0');

    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const addPaymentDate = () => {
    const paymentDate = {
        date: currentDate(),
        is_paid: false,
        xero_account_id: null,
    };

    if (usesConversionRate.value) {
        paymentDates.value.push({
            ...paymentDate,
            amount_target: Math.max(remainingBalance(), 0),
            conversion_rate: invoice.value.conversion_rate,
        });
        return;
    }

    paymentDates.value.push({
        ...paymentDate,
        amount: Math.max(remainingBalance(), 0),
    });
};

const remainingBalance = () => invoiceAmount.value - totalPayments.value - totalCredits.value;

const removePaymentDate = (index) => {
    paymentDates.value.splice(index, 1);
};

const openCurrencyConverter = () => {
    Eventbus.$emit('currencyConverter');
};

defineExpose({
    openModal,
});
</script>

<style lang="scss" scoped>
.payment-dates-table {
    border-collapse: separate;
    border-spacing: 0;
    color: #1f2937;

    thead th {
        background: #f8fafc;
        border-bottom: 1px solid #e2e8f0;
        color: #64748b;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.06em;
        padding: 12px 14px;
        text-align: left;
        text-transform: uppercase;
        white-space: nowrap;
    }

    thead th:first-child {
        padding-left: 20px;
    }

    thead th:last-child {
        padding-right: 20px;
    }

    tbody td {
        border-bottom: 1px solid #eef2f7;
        padding: 14px;
        vertical-align: middle;
    }

    tbody td:first-child {
        padding-left: 20px;
    }

    tbody td:last-child {
        padding-right: 20px;
    }

    tbody tr {
        background: #ffffff;
        transition: background-color 150ms ease;
    }

    tbody tr:hover {
        background: #f8fafc;
    }

    tbody tr:last-child td {
        border-bottom: 0;
    }
}
</style>
